/* global gtag */
import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  CssBaseline,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { showAppAlert } from "../../../store/reducers/appreducer";
import Report from "../Report/Report";
import { useUploadContext } from "../../../store/uploadContext";
import { uploadResumeAPI } from "../../../services/resumeService";
import { DateTime } from "luxon";
import {
  Root,
  JobCard,
  JobIcon,
  JobInfo,
  JobTitle,
  JobSubtitle,
  JobDescription,
  JobMeta,
  JobMetaItem,
  StatusBadge,
  DropzoneContainer,
  UploadIcon,
  DropzoneTitle,
  DropzoneSubtitle,
  SelectFilesButton,
  StyledButton,
  ProcessingContainer,
} from "../../../components/ApplicationComponents";
import { paths } from "../../../routes/paths";
import { useNavigate } from "react-router-dom";
import {
  Description,
  LocationOn,
  Schedule,
  CloudUpload,
} from "@mui/icons-material";

const Application = ({ onNavigate, job }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    uploading,
    setUploading,
    progress,
    setProgress,
    socketConnected,
    scoringComplete,
    displayText,
    setDisplayText,
    startProcessing,
    forceScoring,
    isCurrentlyProcessing,
    getCurrentJobId,
    getCurrentProgress,
    resetProcessingState,
  } = useUploadContext();

  const [selectedJob, setSelectedJob] = useState(null);
  const [error, setError] = useState("");
  const [noUpload, setNoUpload] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showProcessingUI, setShowProcessingUI] = useState(false);

  const Job = useSelector((state) => state.appreducer.selectedJob);
  const jobId = Job ? Job["Job Id"] : null;

  // Function to calculate time ago using Luxon
  const getTimeAgo = (dateInput) => {
    if (!dateInput) return "Date not available";

    try {
      let dateTime;

      // Handle the "2025-07-09" format from the backend
      if (typeof dateInput === "string") {
        // Check if it's a simple date format (YYYY-MM-DD)
        if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Parse as local date
          dateTime = DateTime.fromISO(dateInput, { zone: "local" });

          // For date-only format, we need to compare dates not exact times
          const now = DateTime.now();
          const postingDate = dateTime.toISODate(); // Get just the date part
          const todayDate = now.toISODate(); // Get just today's date part

          // Calculate difference in days
          const diffInDays = Math.floor(now.diff(dateTime, "days").days);

          if (postingDate === todayDate) {
            return "today";
          } else if (diffInDays === 1) {
            return "yesterday";
          } else if (diffInDays === -1) {
            return "tomorrow";
          } else if (diffInDays > 1) {
            return `${diffInDays} days ago`;
          } else if (diffInDays < -1) {
            return `in ${Math.abs(diffInDays)} days`;
          }
        } else {
          // Try to parse as ISO string with time
          dateTime = DateTime.fromISO(dateInput);
        }
      } else if (typeof dateInput === "number") {
        // Handle timestamp
        dateTime = DateTime.fromMillis(dateInput);
      } else if (dateInput instanceof Date) {
        // Handle Date object
        dateTime = DateTime.fromJSDate(dateInput);
      } else {
        return "Invalid date format";
      }

      // Validate the date
      if (!dateTime.isValid) {
        console.error(
          "Invalid date:",
          dateInput,
          "Error:",
          dateTime.invalidReason
        );
        return "Invalid date";
      }

      // For non-date-only formats, use regular relative time
      const relativeTime = dateTime.toRelative();

      return relativeTime || "Date calculation error";
    } catch (error) {
      console.error("Error calculating time ago:", error, "Input:", dateInput);
      return "Date error";
    }
  };

  // Reset processing state when job changes
  useEffect(() => {
    if (jobId && getCurrentJobId() !== jobId) {
      resetProcessingState();
    }
  }, [jobId, getCurrentJobId, resetProcessingState]);

  // Track processing state from context
  useEffect(() => {
    if (!jobId) return;

    const isProcessing = isCurrentlyProcessing() && jobId === getCurrentJobId();
    const currentProgress = getCurrentProgress();

    setLoading(isProcessing);
    setShowProcessingUI(isProcessing);
    setProgress(currentProgress);

    if (currentProgress >= 100) {
      setLoading(false);
      setShowProcessingUI(false);
    }
  }, [
    jobId,
    isCurrentlyProcessing,
    getCurrentJobId,
    getCurrentProgress,
    setProgress,
  ]);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setError("");
      setUploading(true);
      setProgress(0);
      setNoUpload(false);
      setLoading(true);
      setShowProcessingUI(true);

      dispatch(
        showAppAlert({
          show: true,
          alertType: "warning",
          alertMessage: "Resume Upload in Progress",
        })
      );

      setDisplayText("Uploading Resume, Please Wait.");

      if (!Job || !jobId) {
        setError("No job selected. Please select a job first.");
        setUploading(false);
        setLoading(false);
        setShowProcessingUI(false);
        return;
      }

      const newResumes = acceptedFiles.filter(
        (file) => file.type === "application/pdf"
      );

      if (newResumes.length !== acceptedFiles.length) {
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage:
              "Some files were not valid PDFs and were not uploaded.",
          })
        );
        setUploading(false);
        setLoading(false);
        setNoUpload(true);
        setShowProcessingUI(false);
        return;
      }

      try {
        // Upload resumes
        await uploadResumeAPI(jobId, newResumes, (progress) => {
          setProgress(progress);
        });

        setDisplayText("Resume Upload Completed, Processing Started!");
        setProgress(0);

        const uploadedResumes = newResumes.map((file) => file.name);

        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Resume processing started...",
          })
        );

        // Start processing
        startProcessing(jobId, uploadedResumes, dispatch);
      } catch (error) {
        setError(error.message);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "error",
            alertMessage: error.message,
          })
        );
        setLoading(false);
        setShowProcessingUI(false);
      } finally {
        setUploading(false);
      }
    },
    [
      dispatch,
      Job,
      jobId,
      setUploading,
      setProgress,
      setDisplayText,
      startProcessing,
    ]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "application/pdf",
    multiple: true,
  });

  const handleReportClick = () => {
    if (typeof gtag === "function") {
      gtag("event", "button_click", {
        event_category: "User Interaction",
        event_label: "View Report Button",
      });
    }
    navigate(`${paths.dashboard.reports}/${jobId}`);
    setSelectedJob(job);
  };

  const getStatusInfo = () => {
    const isProcessing = isCurrentlyProcessing() && jobId === getCurrentJobId();

    if (scoringComplete) {
      return { status: "ready", text: "Ready for resume upload", icon: "✓" };
    } else if (isProcessing) {
      return { status: "processing", text: "Processing", icon: "⏳" };
    } else {
      return { status: "ready", text: "Ready for resume upload", icon: "" };
    }
  };

  if (!Job) {
    return (
      <Root>
        <CssBaseline />
        <Typography variant="h5">
          Select a job from Job Management Section
        </Typography>
      </Root>
    );
  }

  if (selectedJob) {
    return <Report job={selectedJob} />;
  }

  const isProcessing = isCurrentlyProcessing() && jobId === getCurrentJobId();
  const currentProgress = getCurrentProgress();
  const statusInfo = getStatusInfo();

  return (
    <Root>
      <CssBaseline />

      {/* Job Details Card */}
      <JobCard>
        <JobIcon>
          <Description />
        </JobIcon>
        <JobInfo>
          <JobTitle>{Job["Job Title"]}</JobTitle>
          <JobSubtitle>{Job["Company Name"]}</JobSubtitle>
          <JobDescription>{Job.description}</JobDescription>
          <JobMeta>
            <JobMetaItem>
              <LocationOn fontSize="small" />
              <span>{Job.Location || "Location not specified"}</span>
            </JobMetaItem>
            <JobMetaItem>
              <Schedule fontSize="small" />
              <span>{getTimeAgo(Job["Posting Date"])}</span>
            </JobMetaItem>
          </JobMeta>
        </JobInfo>
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            minHeight: "120px",
            width: "200px",
          }}
        >
          <StatusBadge status={statusInfo.status}>
            {statusInfo.icon && (
              <span style={{ marginRight: "4px" }}>{statusInfo.icon}</span>
            )}
            {statusInfo.text}
          </StatusBadge>
          <div
            style={{
              position: "absolute",
              bottom: "0",
              right: "0",
            }}
          >
            <Tooltip
              title={
                scoringComplete
                  ? "View Rankings based on Resume Score"
                  : isProcessing
                  ? "Force Complete Scoring and View Report"
                  : "Upload resumes first"
              }
              arrow
              placement="top"
            >
              <span>
                <StyledButton
                  onClick={handleReportClick}
                  variant="outlined"
                  size="small"
                  sx={{
                    minWidth: "100px",
                    height: "32px",
                    fontSize: "12px",
                    fontWeight: 500,
                    borderColor: "#4f46e5",
                    color: "#4f46e5",
                    backgroundColor: "transparent",
                    "&:hover": {
                      backgroundColor: "#4f46e5",
                      color: "white",
                    },
                  }}
                >
                  📊 View Report
                </StyledButton>
              </span>
            </Tooltip>
          </div>
        </div>
      </JobCard>

      {/* Upload Section */}
      {isProcessing ? (
        <ProcessingContainer>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {displayText || "Processing resumes..."}
          </Typography>

          {isProcessing && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              You can leave this page and check back later. Processing will
              continue in the background.
            </Typography>
          )}
        </ProcessingContainer>
      ) : (
        <DropzoneContainer {...getRootProps()}>
          <input {...getInputProps()} />
          <UploadIcon>
            <CloudUpload />
          </UploadIcon>
          <DropzoneTitle>Drop your resumes here</DropzoneTitle>
          <DropzoneSubtitle>or click to browse (PDF only)</DropzoneSubtitle>
          <SelectFilesButton>📁 Select Files</SelectFilesButton>
        </DropzoneContainer>
      )}

      {uploading && (
        <ProcessingContainer>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ width: "100%", mb: 2 }}
          />
          <Typography variant="body2" color="textSecondary">
            Upload Progress: {progress}%
          </Typography>
        </ProcessingContainer>
      )}

      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Root>
  );
};

export default Application;
